import { useEffect, useState } from "react"
//components
import { Button } from "../components/ui/buttons/Button";
import { Input } from "../components/ui/inputs/Input";
import { InputContainer } from "../components/ui/inputs/InputContainers";
import  Label  from "../components/ui/labels/Label"
import { ForgotPasswordModal } from "../components/ui/modals/ForgotPassword";
//hooks
import { useNavigate, useLocation, Navigate } from "react-router-dom"
import { useLoading } from "../contexts/LoadingContext"
import { useUser } from "../contexts/UserContext";
import { useError } from "../contexts/ErrorContext";

//api
import { loginAPI } from "../services/authAPI"
import { APIMessage } from "../components/ui/messages/APIMessage";

/**
 * Basic login page. Can also accept a link that includes a job id. If a user logs in here, they are given access to the job.
 * 
 * @returns {JSX.Element}
 * 
 * @category Pages
 */
export const Login = () => {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")

    const [validationErrors, setValidationErrors] = useState({})
    // const [submitError, setSubmitError] = useState("")
    
    const { showLoading, hideLoading } = useLoading()
    const [showModal, setShowModal] = useState(false)
    const {showError} =  useError()
    const { login, user } = useUser()
    const location = useLocation()
    const navigate = useNavigate()

    const navigateLink = location.pathname.includes("accessjob") ? `/signup/accessjob${location.search}` : "/signup"

    //get job unique identifier from url and remove prefix
    const accessJobID = location.pathname.includes("accessjob") ? location.search.split("?job=")[1] : null

    const validate = () => {
        const errors = {}

        if(!email){
            errors.email = "Email is required"
        }

        if(!password){
            errors.password = "Please enter your password"
        }
        
        //if there are errors, set state to errors and return false
        if(Object.keys(errors).length > 0){
            setValidationErrors(errors)
            showError("Could not log in - some form fields are missing")
            return false
        }

        setValidationErrors({})

        return true
    }

    const handleSubmit = async(e) => {
        e.preventDefault()
        showLoading("Logging in...")

        if(!validate()){
            hideLoading()
            return
        }

        const response = await loginAPI({
            Email: email,
            Password: password,
            accessJobID
        })  

        
        if(!response.success){
            showError(response.message)

            setPassword("")

            hideLoading()

            return
        }

        // Cookie is set by the server, login() fetches user data from /auth/me
        // Navigation is handled by useEffect when user state updates
        await login()
        hideLoading()
    }

    useEffect(() => {
        document.title =  `MarTrak - Login`
    }, []);

    // If already authenticated, redirect to home
    if (user) {
        return <Navigate to="/" replace />
    }

    return (
        <>
            <form className="flex items-center justify-center bg-white mb-10" onSubmit={(e) => handleSubmit(e)}>
                <div className="flex flex-col p-8 space-y-4 rounded-lg shadow-2xl bg-white mx-4 mb-0">
                    <p className="text-lg font-medium text-center text-gray-700">
                        Sign in to your account
                    </p>
                    <InputContainer>
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            required
                            placeholder="Enter your email"
                            onChange={(e)=> setEmail(e.target.value)}
                            error={validationErrors.email}
                            value={email}
                        />
                        <APIMessage message={validationErrors.email} isError={true}/>
                    </InputContainer>

                    <InputContainer>
                        <Label htmlFor="password">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            required
                            placeholder="Enter your password"
                            onChange={(e)=> setPassword(e.target.value)}
                            error={validationErrors.password}
                            value={password}
                        />
                        <p onClick={() => setShowModal(true)} className="text-sm text-purple-600 hover:text-purple-700 hover:cursor-pointer hover:underline self-end">Forgot password?</p> 

                        <APIMessage message={validationErrors.password} isError={true}/>

                    </InputContainer>
                    
                    <Button type="submit">Log In</Button>

                    <p onClick={() => navigate(navigateLink)} className="text-md text-purple-700 hover:text-purple-800 hover:cursor-pointer self-center pt-4">Don't have an account? <span className="underline">Sign up</span></p> 

                </div>
            </form>

            {showModal 
                ? <ForgotPasswordModal setShowModal={setShowModal}/>

                : null
            }
        </>
    );
}
